/**
 * @fileoverview Connect promotion-batch selection to recorded scoring, member inspection, and explicit rerun/restoration actions. The backend owns batch membership and execution state; this workspace owns pagination, selected lead preview, and queue navigation. `IntakeQueue.js` and `Queue/intakeQueueUtils.js` supply shared lead presentation. Reading a batch or its score does not admit another scoring run.
 */

import React, { Fragment, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import ToastContext from '../../ToastContext';
import AppIcon from '../shared/AppIcon';
import {
  fetchIntakeAutomationJobs,
  fetchIntakeLeadPreview,
  fetchIntakePromotionBatch,
  fetchIntakePromotionBatches,
  resetIntakeLeadPipeline,
  runIntakePipeline,
  runIntakeStage,
} from './intakeApi';
import {
  formatDate,
  getPromotionBatchStatusClass,
  getPromotionBatchStatusLabel,
  titleize,
} from './intakeFormatters';
import IntakeViewSwitch from './IntakeViewSwitch';
import { IntakeLeadPreview, IntakeRunButton } from './IntakeQueue';
import {
  CLOSED_INTAKE_STATUSES,
  RUN_REMAINING_STOP_STAGES,
  applyRunRemainingResultState,
  getPreviewCurrentStage,
  getLeadStageLabel,
  hasLeadActivePipelineWork,
  hasPreviewActivePipelineWork,
} from './Queue/intakeQueueUtils';
import { ACTIVE_STATUSES, COMPLETE_STATUSES, FAILED_RUN_STATUSES, REVIEW_RUN_STATUSES } from './intakeStatuses';
import { readIntakeViewState, writeIntakeViewState } from './intakeWorkspaceState';
import { useAssignedAttorneyReviewCount } from './useAssignedAttorneyReviewCount';
import './Intake.css';

const BATCH_PAGE_SIZE = 25;
const MEMBER_PAGE_SIZE = 100;
const AUTOMATION_JOB_ID = 'intake_batch_scoring_automation';
const RESOLVED_BATCH_STATUSES = new Set(['active_class_action_bypass', 'ineligible_for_batch_ranking', 'archived_stale', 'manual_override']);
const FILTERS = ['all', 'open', 'closed'];
const EMPTY_MEMBER_FILTERS = { defendant: '', claimType: '', state: '' };
const SCORE_COMPONENTS = [
  ['case_strength', 'Merit', 30],
  ['practical_recovery', 'Recovery', 25],
  ['defendant_quality', 'Defendant', 15],
  ['attorney_appetite_match', 'Firm fit', 20],
  ['information_completeness', 'Completeness', 10],
];



function phaseLabel(phase) {
  if (phase === 'ranked') return 'Ranked';
  if (phase === 'ranking') return 'Ranking';
  if (phase === 'scoring') return 'Scoring';
  if (phase === 'needs_review') return 'Needs review';
  return 'Open';
}



function relativeCloseTime(value) {
  if (!value) return 'No scheduled close';
  const milliseconds = new Date(value).getTime() - Date.now();
  if (Number.isNaN(milliseconds)) return 'No scheduled close';
  if (milliseconds <= 0) return 'Close time reached';
  const minutes = Math.ceil(milliseconds / 60000);
  if (minutes < 60) return `${minutes}m remaining`;
  const hours = Math.ceil(minutes / 60);
  if (hours < 48) return `${hours}h remaining`;
  return `${Math.ceil(hours / 24)}d remaining`;
}



function leadName(lead) {
  return lead?.prospect_name || lead?.lead_title || `Lead #${lead?.id || ''}`;
}



function isAwaitingAttorneyReview(lead) {
  return lead?.status === 'ready_for_decision' && lead?.next_action_label === 'Attorney review pending';
}



function isResolvedBatchMember(member) {
  return RESOLVED_BATCH_STATUSES.has(member?.display_status) || Boolean(member?.resolution_reason);
}



function metricValue(batch, status) {
  return Number(batch?.outcome_counts?.[status] || 0);
}



function latestRunAllResult(result) {
  let latest = result || {};
  let guard = 0;
  while (latest?.run_result?.manual_pipeline_continuation && guard < 20) {
    latest = latest.run_result.manual_pipeline_continuation;
    guard += 1;
  }
  return latest || result || {};
}



function BatchPager({ page, pages, onPageChange, label }) {
  if (pages <= 1) return null;
  return (
    <div className="intake-batches__pager" aria-label={label}>
      <button type="button" className="intake-button" disabled={page <= 1} onClick={() => onPageChange(page - 1)}>
        <AppIcon icon="pi pi-chevron-left" aria-hidden="true" />
        Previous
      </button>
      <span>Page {page} of {pages}</span>
      <button type="button" className="intake-button" disabled={page >= pages} onClick={() => onPageChange(page + 1)}>
        Next
        <AppIcon icon="pi pi-chevron-right" aria-hidden="true" />
      </button>
    </div>
  );
}



function BatchLeadDrawer({
  lead,
  payload,
  loading,
  error,
  runningPipelineLeadId,
  runningStageLeadId,
  onClose,
  onRunNextStage,
  onRunRemaining,
  runNextStageDisabledReason,
  runRemainingDisabledReason,
  batchMember,
  onRestoreToIntake,
  restoringLeadId,
}) {
  const [showRestoreForm, setShowRestoreForm] = useState(false);
  const [restoreReason, setRestoreReason] = useState('');
  useEffect(

  () => {
    if (!lead) return undefined;
    const closeOnEscape =

    (event) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', closeOnEscape);
    return () => window.removeEventListener('keydown', closeOnEscape);
  }, [lead, onClose]);

  if (!lead) return null;
  const pendingAttorneyReview = (payload?.attorney_review_assignments || []).find((assignment) => assignment.status === 'pending');
  const handoffNote = pendingAttorneyReview?.case_presentation?.handoff_note;

  return (
    <div
      className="intake-lead-preview-drawer-backdrop"
      data-testid="batch-lead-drawer-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}>
      <aside
        className="intake-lead-preview-drawer"
        role="dialog"
        aria-modal="true"
        aria-label={leadName(lead)}>
        <header className="intake-lead-preview-drawer__header">
          <span>Lead preview</span>
          <button type="button" className="intake-icon-button" aria-label="Close lead preview" onClick={onClose}>
            <AppIcon icon="pi pi-times" aria-hidden="true" />
          </button>
        </header>
        {batchMember?.resolution_reason && (
          <section className="intake-batch-ineligibility" aria-label="Batch ranking status">
            <span>{batchMember.resolution_label || 'Resolved'}</span>
            {batchMember.resolution_outcome && <strong>{batchMember.resolution_outcome}</strong>}
            <p>{batchMember.resolution_reason}</p>
            {batchMember.historical_batch_rank && (
              <small>Historical batch rank #{batchMember.historical_batch_rank}; no longer active.</small>
            )}
            {lead.converted_matter_number && (
              <Link className="intake-stage-link" to={`/case_details/${lead.converted_matter_number}`}>
                Open Matter {lead.converted_matter_number}
              </Link>
            )}
            {batchMember.missing_matter_link && <small>No linked Matter number is recorded in Intake.</small>}
            {batchMember.restore_to_intake_available ? (
              showRestoreForm ? (
                <div className="intake-batch-ineligibility__restore">
                  <label htmlFor="batch-restore-reason">Why should this lead return to Intake?</label>
                  <textarea
                    id="batch-restore-reason"
                    value={restoreReason}
                    onChange={(event) => setRestoreReason(event.target.value)}
                    placeholder="Corrected status or reason for reopening"
                    rows="3" />
                  <div>
                    <button type="button" className="intake-button" onClick={() => setShowRestoreForm(false)} disabled={restoringLeadId === lead.id}>Cancel</button>
                    <button
                      type="button"
                      className="intake-button intake-button--primary"
                      disabled={!restoreReason.trim() || restoringLeadId === lead.id}
                      onClick={() => onRestoreToIntake(lead, restoreReason.trim())}>
                      {restoringLeadId === lead.id ? 'Restoring…' : 'Restore to Intake'}
                    </button>
                  </div>
                </div>
              ) : (
                <button type="button" className="intake-button" onClick={() => setShowRestoreForm(true)}>Restore to Intake</button>
              )
            ) : (
              <small>This lead is linked to a Matter and cannot be restored from Intake.</small>
            )}
          </section>
        )}
        {isAwaitingAttorneyReview(lead) && (
          <section className="intake-batch-ineligibility" aria-label="Attorney review handoff">
            <span>Attorney review</span>
            <strong>{pendingAttorneyReview?.attorney_name || pendingAttorneyReview?.attorney_email || 'Attorney assignment pending'}</strong>
            {handoffNote && <p>{handoffNote}</p>}
            {pendingAttorneyReview?.id && (
              <Link className="intake-button intake-button--primary" to={`/intake/reviews/${pendingAttorneyReview.id}`}>
                Open case review
              </Link>
            )}
          </section>
        )}
        <IntakeLeadPreview
          lead={lead}
          payload={payload}
          loading={loading}
          error={error}
          runningPipelineLeadId={runningPipelineLeadId}
          runningStageLeadId={runningStageLeadId}
          onRunNextStage={onRunNextStage}
          onRunRemaining={onRunRemaining}
          runNextStageDisabledReason={runNextStageDisabledReason}
          runRemainingDisabledReason={runRemainingDisabledReason}
          readOnly={Boolean(batchMember?.resolution_reason) || isAwaitingAttorneyReview(lead)}
          batchResolutionLabel={batchMember?.resolution_label || ''} />
      </aside>
    </div>
  );
}



function OpenBatchCard({ batch }) {
  if (!batch) {
    return (
      <section className="intake-batch-progress intake-batch-progress--empty" aria-label="Open batch status">
        <AppIcon icon="pi pi-inbox" aria-hidden="true" />
        <span>No open batch</span>
      </section>
    );
  }
  const capacity = Math.max(0, Number(batch.max_leads || 0));
  const count = Math.max(0, Number(batch.lead_count || 0));
  const percent = capacity ? Math.min(100, Math.round((count / capacity) * 100)) : 0;
  return (
    <section className="intake-batch-progress" aria-label={`Open batch ${batch.id} progress`}>
      <div className="intake-batch-progress__heading">
        <div>
          <span className="intake-batch-progress__eyebrow">Open batch</span>
          <strong>Batch #{batch.id}</strong>
        </div>
        <span className="intake-batch-phase intake-batch-phase--open">{phaseLabel(batch.phase)}</span>
      </div>
      <div className="intake-batch-progress__numbers">
        <strong>{count.toLocaleString()} / {capacity.toLocaleString()}</strong>
        <span>{relativeCloseTime(batch.scheduled_close_at)}</span>
      </div>
      <div
        className="intake-batch-progress__track"
        role="progressbar"
        aria-valuemin="0"
        aria-valuemax={capacity}
        aria-valuenow={count}
        aria-label={`${count} of ${capacity} lead capacity`}>
        <span style={{ width: `${percent}%` }} />
      </div>
      <span className="intake-batch-progress__date">Scheduled {formatDate(batch.scheduled_close_at)}</span>
    </section>
  );
}



function BatchRail({ batches, selectedId, status, loading, onFilterChange, onSelect, page, pages, onPageChange }) {
  return (
    <aside className="intake-batch-rail" aria-label="Promotion batches">
      <div className="intake-batch-filters" role="group" aria-label="Batch status filter">
        {FILTERS.map((filter) => (
          <button
            type="button"
            key={filter}
            aria-pressed={status === filter}
            className={`intake-batch-filter${status === filter ? ' intake-batch-filter--active' : ''}`}
            onClick={() => onFilterChange(filter)}>
            {titleize(filter)}
          </button>
        ))}
      </div>
      <div className="intake-batch-rail__list">
        {loading && batches.length === 0 && <div className="intake-batches__rail-state">Loading batches…</div>}
        {!loading && batches.length === 0 && <div className="intake-batches__rail-state">No {status === 'all' ? '' : `${status} `}batches</div>}
        {batches.map((batch) => (
          <button
            type="button"
            key={batch.id}
            aria-current={Number(selectedId) === batch.id ? 'true' : undefined}
            className={`intake-batch-rail__item${Number(selectedId) === batch.id ? ' intake-batch-rail__item--active' : ''}`}
            onClick={() => onSelect(batch.id)}>
            <span className="intake-batch-rail__row">
              <strong>Batch #{batch.id}</strong>
              <span className={`intake-batch-phase intake-batch-phase--${batch.phase}`}>{phaseLabel(batch.phase)}</span>
            </span>
            <span className="intake-batch-rail__row intake-batch-rail__meta">
              <span>{batch.lead_count} leads</span>
              <span>{batch.status === 'open' ? relativeCloseTime(batch.scheduled_close_at) : formatDate(batch.closed_at)}</span>
            </span>
          </button>
        ))}
      </div>
      <BatchPager page={page} pages={pages} onPageChange={onPageChange} label="Batch list pagination" />
    </aside>
  );
}



function BatchDetail({
  payload,
  loading,
  error,
  expandedMemberId,
  memberFilters,
  memberSort,
  onMemberFilterChange,
  onClearMemberFilters,
  onMemberSortChange,
  onToggleRationale,
  onMemberPageChange,
  onSelectLead,
  selectedLeadId,
  runningPipelineLeadId,
  onRunRemaining,
  runRemainingDisabledReason,
}) {
  if (loading && !payload) return <div className="intake-batch-detail__state">Loading batch…</div>;
  if (error) return <div className="intake-batch-detail__state intake-batch-detail__state--error">{error}</div>;
  if (!payload?.batch) return <div className="intake-batch-detail__state">Select a batch to inspect its members.</div>;

  const { batch, members = [] } = payload;
  const filterOptions = payload.filter_options || {};
  const hasActiveFilters = Object.values(memberFilters).some(Boolean);
  const unrankedIndex = members.findIndex((member) => (
    !RESOLVED_BATCH_STATUSES.has(member.display_status)
    && (member.batch_rank === null || member.batch_rank === undefined)
  ));
  const cutoffIndex = members.findIndex((member) => Number(member.batch_rank) > Number(batch.outreach_limit || 0));
  const resolvedIndex = members.findIndex((member) => isResolvedBatchMember(member));
  return (
    <section className="intake-batch-detail" aria-labelledby="intake-batch-detail-title">
      <header className="intake-batch-detail__header">
        <div>
          <div className="intake-batch-detail__title-row">
            <h2 id="intake-batch-detail-title">Batch #{batch.id}</h2>
            <span className={`intake-batch-phase intake-batch-phase--${batch.phase}`}>{phaseLabel(batch.phase)}</span>
          </div>
          <p>
            Opened {formatDate(batch.opened_at)}
            {batch.closed_at ? ` · Closed ${formatDate(batch.closed_at)}` : ''}
            {batch.close_reason ? ` · ${titleize(batch.close_reason)}` : ''}
          </p>
        </div>
        <span className="intake-batch-detail__limit">Outreach cutoff: {batch.outreach_limit}</span>
      </header>
      <div className="intake-batch-metrics" aria-label="Batch summary">
        <div><strong>{batch.lead_count}</strong><span>Leads</span></div>
        <div><strong>{batch.scored_count}</strong><span>Scored</span></div>
        <div><strong>{batch.resolved_count}</strong><span>Resolved</span></div>
        <div><strong>{metricValue(batch, 'promoted_for_outreach')}</strong><span>Promoted</span></div>
        <div><strong>{batch.unranked_count}</strong><span>Unranked</span></div>
      </div>
      <div className="intake-batch-member-controls" aria-label="Batch member filters">
        <label>
          <span>Defendant</span>
          <select value={memberFilters.defendant} onChange={(event) => onMemberFilterChange('defendant', event.target.value)}>
            <option value="">All defendants</option>
            {(filterOptions.defendants || []).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label>
          <span>Claim type</span>
          <select value={memberFilters.claimType} onChange={(event) => onMemberFilterChange('claimType', event.target.value)}>
            <option value="">All claim types</option>
            {(filterOptions.claim_types || []).map((value) => <option key={value} value={value}>{titleize(value)}</option>)}
          </select>
        </label>
        <label>
          <span>State</span>
          <select value={memberFilters.state} onChange={(event) => onMemberFilterChange('state', event.target.value)}>
            <option value="">All states</option>
            {(filterOptions.states || []).map((value) => <option key={value} value={value}>{value}</option>)}
          </select>
        </label>
        <label className="intake-batch-member-controls__sort">
          <span>Sort</span>
          <select value={memberSort} onChange={(event) => onMemberSortChange(event.target.value)}>
            <option value="rank">Batch rank</option>
            <option value="score">Score · High to low</option>
          </select>
        </label>
        {hasActiveFilters && (
          <button type="button" className="intake-batch-member-controls__clear" onClick={onClearMemberFilters}>
            <AppIcon icon="pi pi-times" aria-hidden="true" />
            Clear filters
          </button>
        )}
      </div>
      <div className="intake-batch-table-wrap">
        <table className="intake-batch-table">
          <thead>
            <tr>
              <th scope="col">Actions</th>
              <th scope="col">Rank</th>
              <th scope="col">Lead</th>
              <th scope="col">Defendant</th>
              <th scope="col">Claim type</th>
              <th scope="col">State</th>
              <th scope="col">Score</th>
              <th scope="col">Outcome</th>
              <th scope="col">Stage</th>
              <th scope="col">Received</th>
            </tr>
          </thead>
          <tbody>
            {members.length === 0 && (
              <tr><td colSpan="10" className="intake-batch-table__empty">{hasActiveFilters ? 'No members match these filters.' : 'No members in this batch.'}</td></tr>
            )}
            {members.map((member, index) => {
              const rationaleOpen = expandedMemberId === member.id;
              const hasScoreDetails = Boolean(member.score_rationale || member.score_components);
              return (
                <Fragment key={member.id}>
                  {index === cutoffIndex && (
                    <tr className="intake-batch-table__divider intake-batch-table__divider--cutoff">
                      <td colSpan="10"><span>Below outreach cutoff</span></td>
                    </tr>
                  )}
                  {index === unrankedIndex && (
                    <tr className="intake-batch-table__divider intake-batch-table__divider--unranked">
                      <td colSpan="10"><span>Unranked</span></td>
                    </tr>
                  )}
                  {index === resolvedIndex && (
                    <tr className="intake-batch-table__divider intake-batch-table__divider--resolved">
                      <td colSpan="10"><span>Resolved leads</span></td>
                    </tr>
                  )}
                  <tr
                    className={`intake-batch-table__member${member.within_outreach_cutoff ? ' intake-batch-table__member--outreach' : ''}${String(selectedLeadId || '') === String(member.lead?.id || '') ? ' intake-batch-table__member--selected' : ''}`}
                    tabIndex={member.lead ? 0 : undefined}
                    aria-label={member.lead ? `Open ${leadName(member.lead)}` : undefined}
                    onClick={(event) => {
                      if (!member.lead || event.target.closest('a, button')) return;
                      onSelectLead(member.lead.id);
                    }}
                    onKeyDown={(event) => {
                      if (!member.lead || event.target !== event.currentTarget || !['Enter', ' '].includes(event.key)) return;
                      event.preventDefault();
                      onSelectLead(member.lead.id);
                    }}>
                    <td className="intake-batch-table__actions">
                      {member.lead && !isAwaitingAttorneyReview(member.lead) && !isResolvedBatchMember(member) && (
                        <IntakeRunButton
                          lead={member.lead}
                          table
                          runningPipelineLeadId={runningPipelineLeadId}
                          onRunRemaining={onRunRemaining}
                          runRemainingDisabledReason={runRemainingDisabledReason} />
                      )}
                    </td>
                    <td className="intake-batch-table__rank">{member.batch_rank ? `#${member.batch_rank}` : '—'}</td>
                    <td>
                      {member.lead ? (
                        <Link
                          className="intake-batch-table__lead"
                          to={`/intake/leads/${member.lead.id}`}
                          onClick={(event) => {
                            event.preventDefault();
                            onSelectLead(member.lead.id);
                          }}>
                          {leadName(member.lead)}
                        </Link>
                      ) : <span>Lead unavailable</span>}
                    </td>
                    <td>{member.lead?.defendant_name || '—'}</td>
                    <td>{member.lead?.category ? titleize(member.lead.category) : '—'}</td>
                    <td>{member.lead?.state || '—'}</td>
                    <td>
                      <div className="intake-batch-score">
                        <span>{member.score === null || member.score === undefined ? '—' : Number(member.score).toFixed(1)}</span>
                        {hasScoreDetails && (
                          <button
                            type="button"
                            className="intake-batch-score__rationale"
                            aria-label={`${rationaleOpen ? 'Hide' : 'Show'} score rationale for ${leadName(member.lead)}`}
                            aria-expanded={rationaleOpen}
                            onClick={() => onToggleRationale(member.id)}>
                            <AppIcon icon={rationaleOpen ? 'pi pi-chevron-up' : 'pi pi-info-circle'} aria-hidden="true" />
                          </button>
                        )}
                      </div>
                    </td>
                    <td><span className={`intake-badge ${getPromotionBatchStatusClass(member.display_status)}`}>{member.resolution_label || getPromotionBatchStatusLabel(member.display_status)}</span></td>
                    <td>{isAwaitingAttorneyReview(member.lead)
                      ? <span className="intake-badge intake-badge--info">Awaiting attorney review</span>
                      : getLeadStageLabel(member.lead)}</td>
                    <td>{formatDate(member.received_at)}</td>
                  </tr>
                  {rationaleOpen && (
                    <tr className="intake-batch-table__rationale">
                      <td aria-hidden="true" />
                      <td aria-hidden="true" />
                      <td colSpan="8">
                        {member.score_components && (
                          <div className="intake-batch-score-components" aria-label="Weighted score components">
                            {SCORE_COMPONENTS.map(([key, label, weight]) => (
                              <div key={key} className="intake-batch-score-component">
                                <span>{label} · {weight}%</span>
                                <strong>{Number(member.score_components[key] || 0).toFixed(0)}</strong>
                              </div>
                            ))}
                          </div>
                        )}
                        {member.score_rationale && <><strong>Score rationale</strong><p>{member.score_rationale}</p></>}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      <BatchPager
        page={payload.member_page || 1}
        pages={payload.member_pages || 0}
        onPageChange={onMemberPageChange}
        label="Batch member pagination" />
    </section>
  );
}



export default function IntakeBatches() {
  const navigate = useNavigate();
  const showToast = useContext(ToastContext);
  const { batchId } = useParams();
  const { count: assignedReviewCount } = useAssignedAttorneyReviewCount();
  const restoredStateRef = useRef(readIntakeViewState('batches'));
  const restoredState = restoredStateRef.current;
  const restoredBatchMatches = String(restoredState.batchId || '') === String(batchId || '');
  const [status, setStatus] = useState(() => FILTERS.includes(restoredState.status) ? restoredState.status : 'all');
  const [page, setPage] = useState(() => Math.max(1, Number(restoredState.page) || 1));
  const [listPayload, setListPayload] = useState({ batches: [], page: 1, pages: 0, total: 0, available: true });
  const [openBatch, setOpenBatch] = useState(null);
  const [listLoading, setListLoading] = useState(true);
  const [listError, setListError] = useState('');
  const [detailPayload, setDetailPayload] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState('');
  const [memberPage, setMemberPage] = useState(() => restoredBatchMatches ? Math.max(1, Number(restoredState.memberPage) || 1) : 1);
  const [memberFilters, setMemberFilters] = useState(() => restoredBatchMatches ? {
    defendant: restoredState.memberFilters?.defendant || '',
    claimType: restoredState.memberFilters?.claimType || '',
    state: restoredState.memberFilters?.state || '',
  } : EMPTY_MEMBER_FILTERS);
  const [memberSort, setMemberSort] = useState(() => restoredBatchMatches && restoredState.memberSort === 'score' ? 'score' : 'rank');
  const [expandedMemberId, setExpandedMemberId] = useState(() => restoredBatchMatches ? restoredState.expandedMemberId || null : null);
  const [automationJob, setAutomationJob] = useState(undefined);
  const [refreshKey, setRefreshKey] = useState(0);
  const [selectedLeadId, setSelectedLeadId] = useState(null);
  const [previewPayload, setPreviewPayload] = useState(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState('');
  const [runningPipelineLeadId, setRunningPipelineLeadId] = useState(null);
  const [runningStageLeadId, setRunningStageLeadId] = useState(null);
  const [restoringLeadId, setRestoringLeadId] = useState(null);
  const previousBatchIdRef = useRef(batchId);

  const loadLists = useCallback(

  async () => {
    setListLoading(true);
    setListError('');
    try {
      const [listResponse, openResponse] = await Promise.all([
        fetchIntakePromotionBatches({ status, page, page_size: BATCH_PAGE_SIZE }),
        fetchIntakePromotionBatches({ status: 'open', page: 1, page_size: 1 }),
      ]);
      const nextPayload = listResponse.data || {};
      setListPayload({ batches: [], page: 1, pages: 0, total: 0, ...nextPayload });
      setOpenBatch(openResponse.data?.batches?.[0] || null);
    } catch (error) {
      setListError(error.response?.data?.error || 'Unable to load promotion batches.');
    } finally {
      setListLoading(false);
    }
  }, [page, refreshKey, status]);

  useEffect(() => { loadLists(); }, [loadLists]);
  useEffect(() => {
    if (!batchId && !listLoading && listPayload.batches?.length) {
      navigate(`/intake/batches/${listPayload.batches[0].id}`, { replace: true });
    }
  }, [batchId, listLoading, listPayload.batches, navigate]);

  useEffect(() => {
    let active = true;
    fetchIntakeAutomationJobs()
      .then((response) => {
        if (!active) return;
        setAutomationJob((response.data?.jobs || []).find((job) => job.id === AUTOMATION_JOB_ID) || null);
      })
      .catch(() => { if (active) setAutomationJob(null); });
    return () => { active = false; };
  }, [refreshKey]);

  const loadDetail = useCallback(

  async () => {
    if (!batchId) {
      setDetailPayload(null);
      return;
    }
    setDetailLoading(true);
    setDetailError('');
    try {
      const response = await fetchIntakePromotionBatch(batchId, {
        member_page: memberPage,
        member_page_size: MEMBER_PAGE_SIZE,
        sort: memberSort,
        ...(memberFilters.defendant ? { defendant: memberFilters.defendant } : {}),
        ...(memberFilters.claimType ? { claim_type: memberFilters.claimType } : {}),
        ...(memberFilters.state ? { state: memberFilters.state } : {}),
      });
      setDetailPayload(response.data);
    } catch (error) {
      setDetailPayload(null);
      setDetailError(error.response?.status === 404 ? 'Batch not found.' : (error.response?.data?.error || 'Unable to load this batch.'));
    } finally {
      setDetailLoading(false);
    }
  }, [batchId, memberFilters.claimType, memberFilters.defendant, memberFilters.state, memberPage, memberSort, refreshKey]);

  useEffect(() => { loadDetail(); }, [loadDetail]);
  useEffect(() => {
    if (String(previousBatchIdRef.current || '') !== String(batchId || '')) {
      setMemberPage(1);
      setExpandedMemberId(null);
      setMemberFilters(EMPTY_MEMBER_FILTERS);
      setMemberSort('rank');
      setSelectedLeadId(null);
      setPreviewPayload(null);
      setPreviewError('');
    }
    previousBatchIdRef.current = batchId;
  }, [batchId]);

  useEffect(

  () => {
    if (!selectedLeadId) {
      setPreviewPayload(null);
      setPreviewError('');
      setPreviewLoading(false);
      return undefined;
    }
    let cancelled = false;
    setPreviewLoading(true);
    fetchIntakeLeadPreview(selectedLeadId)
      .then((response) => {
        if (cancelled) return;
        setPreviewPayload(response.data);
        setPreviewError('');
      })
      .catch(

      (error) => {
        console.error('Failed to load intake lead preview:', error);
        if (cancelled) return;
        setPreviewPayload(null);
        setPreviewError('Unable to load lead preview.');
      })
      .finally(() => {
        if (!cancelled) setPreviewLoading(false);
      });
    return () => { cancelled = true; };
  }, [selectedLeadId]);

  useEffect(() => {
    if (!batchId) return;
    writeIntakeViewState('batches', {
      batchId,
      status,
      page,
      memberPage,
      memberFilters,
      memberSort,
      expandedMemberId,
    });
  }, [batchId, expandedMemberId, memberFilters, memberPage, memberSort, page, status]);

  const automationState = useMemo(() => {
    if (automationJob === undefined) return { label: 'Checking automation', className: 'intake-batch-automation--checking' };
    if (automationJob?.is_active) return { label: 'Batch automation on', className: 'intake-batch-automation--active' };
    return { label: 'Batch automation off', className: 'intake-batch-automation--inactive' };
  }, [automationJob]);

  const handleFilterChange =

  (nextStatus) => {
    setStatus(nextStatus);
    setPage(1);
  };

  const selectedLead = useMemo(() => (
    selectedLeadId
      ? (detailPayload?.members || [])
        .map((member) => member.lead)
        .find((lead) => String(lead?.id || '') === String(selectedLeadId)) || previewPayload?.lead || null
      : null
  ), [detailPayload?.members, previewPayload?.lead, selectedLeadId]);
  const selectedBatchMember = useMemo(() => (
    selectedLeadId
      ? (detailPayload?.members || []).find((member) => String(member.lead?.id || '') === String(selectedLeadId)) || null
      : null
  ), [detailPayload?.members, selectedLeadId]);

  const updateMemberLead = useCallback(

  (updatedLead) => {
    if (!updatedLead?.id) return;
    setDetailPayload((current) => {
      if (!current?.members) return current;
      return {
        ...current,
        members: current.members.map((member) => (
          member.lead?.id === updatedLead.id
            ? { ...member, lead: { ...member.lead, ...updatedLead } }
            : member
        )),
      };
    });
    setPreviewPayload((current) => {
      if (!current?.lead || current.lead.id !== updatedLead.id) return current;
      return { ...current, lead: { ...current.lead, ...updatedLead } };
    });
  }, []);

  const runRemainingDisabledReason = useCallback(

  (lead) => {
    if (!lead?.id) return 'Lead is not available.';
    if (lead.ai_processing_locked) return lead.ai_processing_lock_reason || 'AI processing is locked for this lead.';
    if (hasLeadActivePipelineWork(lead)) return 'Pipeline stages are already running.';
    if (CLOSED_INTAKE_STATUSES.has(lead.status)) return 'Lead is closed.';
    if (!lead.current_stage) return 'No current pipeline stage is available.';
    if (RUN_REMAINING_STOP_STAGES.has(lead.current_stage)) return 'Pipeline is waiting for a manual decision or conversion step.';
    return '';
  }, []);

  const runNextStageDisabledReason = useCallback(

  (lead, payload) => {
    if (!lead?.id) return 'Lead is not available.';
    if (runningStageLeadId || runningPipelineLeadId) return 'Another pipeline action is running.';
    if (lead.ai_processing_locked) return lead.ai_processing_lock_reason || 'AI processing is locked for this lead.';
    if (hasPreviewActivePipelineWork(payload, lead)) return 'Pipeline stages are already running.';
    if (CLOSED_INTAKE_STATUSES.has(lead.status)) return 'Lead is closed.';
    const currentStage = getPreviewCurrentStage(payload, lead);
    if (!currentStage?.stage?.key) return 'No current pipeline stage is available.';
    if (RUN_REMAINING_STOP_STAGES.has(currentStage.stage.key)) return 'Pipeline is waiting for a manual decision or conversion step.';
    return '';
  }, [runningPipelineLeadId, runningStageLeadId]);

  const runRemainingForLead = useCallback(

  async (event, lead) => {
    event?.stopPropagation?.();
    if (runRemainingDisabledReason(lead) || runningPipelineLeadId || runningStageLeadId) return;
    setRunningPipelineLeadId(lead.id);
    try {
      const response = await runIntakePipeline(lead.id, { reason: 'Manual run remaining pipeline from intake batch' });
      const result = latestRunAllResult(response?.data?.run_all_result);
      if (response?.data?.lead) {
        updateMemberLead(applyRunRemainingResultState(response.data.lead, result));
      }
      const detail = result.message || 'Run remaining pipeline requested.';
      if (result.status === 'blocked') {
        showToast?.({ severity: 'warn', summary: 'Pipeline Paused', detail });
      } else if (result.status === 'completed' || result.status === 'stopped') {
        showToast?.({ severity: 'success', summary: 'Pipeline Stopped', detail });
      } else if (result.status === 'waiting') {
        showToast?.({ severity: 'info', summary: 'Pipeline Armed', detail });
      } else {
        showToast?.({ severity: 'info', summary: 'Pipeline Running', detail });
      }
    } catch (error) {
      console.error('Failed to run remaining intake pipeline:', error);
      showToast?.({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to run remaining pipeline' });
    } finally {
      setRunningPipelineLeadId(null);
    }
  }, [runRemainingDisabledReason, runningPipelineLeadId, runningStageLeadId, showToast, updateMemberLead]);

  const runNextStageForLead = useCallback(

  async (event, lead, payload) => {
    event?.stopPropagation?.();
    const disabledReason = runNextStageDisabledReason(lead, payload);
    if (disabledReason) return;
    const currentStage = getPreviewCurrentStage(payload, lead);
    const stage = currentStage.stage;
    setRunningStageLeadId(lead.id);
    try {
      const response = await runIntakeStage(lead.id, {
        stage_key: stage.key,
        reason: `Manual run next stage from intake batch: ${stage.label || stage.key}`,
      });
      if (response?.data?.lead) updateMemberLead(response.data.lead);
      const detailResponse = await fetchIntakeLeadPreview(lead.id);
      setPreviewPayload(detailResponse.data);
      const status = String(response?.data?.run_result?.status || response?.data?.stage_run?.status || '').toLowerCase();
      const message = response?.data?.run_result?.message || response?.data?.stage_run?.error || `${stage.label || 'Stage'} run requested.`;
      const severity = FAILED_RUN_STATUSES.has(status)
        ? 'error'
        : REVIEW_RUN_STATUSES.has(status)
          ? 'warn'
          : COMPLETE_STATUSES.has(status)
            ? 'success'
            : 'info';
      const summary = FAILED_RUN_STATUSES.has(status)
        ? 'Stage Failed'
        : REVIEW_RUN_STATUSES.has(status)
          ? 'Review Needed'
          : ACTIVE_STATUSES.has(status)
            ? 'Stage Running'
            : COMPLETE_STATUSES.has(status)
              ? 'Stage Complete'
              : 'Stage Requested';
      showToast?.({ severity, summary, detail: message });
    } catch (error) {
      console.error('Failed to run next intake stage:', error);
      showToast?.({ severity: 'error', summary: 'Error', detail: error?.response?.data?.error || 'Failed to run next stage' });
    } finally {
      setRunningStageLeadId(null);
    }
  }, [runNextStageDisabledReason, showToast, updateMemberLead]);

  const openLeadDrawer = useCallback(

  (leadId) => {
    setPreviewPayload(null);
    setPreviewError('');
    setSelectedLeadId(leadId);
  }, []);

  const closeLeadDrawer = useCallback(

  () => {
    setSelectedLeadId(null);
    setPreviewPayload(null);
    setPreviewError('');
  }, []);

  const restoreLeadToIntake = useCallback(

  async (lead, reason) => {
    if (!lead?.id || restoringLeadId) return;
    setRestoringLeadId(lead.id);
    try {
      await resetIntakeLeadPipeline(lead.id, { reason, hold: false });
      showToast?.({ severity: 'success', summary: 'Lead Restored', detail: 'The lead returned to the first Intake stage and is no longer ranked in this batch.' });
      closeLeadDrawer();
      setRefreshKey((key) => key + 1);
    } catch (error) {
      showToast?.({ severity: 'error', summary: 'Restore Failed', detail: error?.response?.data?.error || 'Unable to restore this lead to Intake.' });
    } finally {
      setRestoringLeadId(null);
    }
  }, [closeLeadDrawer, restoringLeadId, showToast]);

  return (
    <div className="intake-page">
      <div className="intake-page__body">
        <section className="intake-queue intake-batches">
          <div className="intake-toolbar intake-batches__toolbar">
            <div className="intake-title">
              <IntakeViewSwitch activeView="batches" assignedReviewCount={assignedReviewCount} />
              <span className="intake-title__count">{Number(listPayload.total || 0).toLocaleString()} batches</span>
            </div>
            <div className="intake-batches__toolbar-actions">
              <span className={`intake-batch-automation ${automationState.className}`}>
                <span aria-hidden="true" />{automationState.label}
              </span>
              {automationJob !== undefined && !automationJob?.is_active && <Link className="intake-batches__automation-link" to="/automations">Automations</Link>}
              <button type="button" className="intake-button" onClick={() => setRefreshKey((key) => key + 1)}>
                <AppIcon icon="pi pi-refresh" aria-hidden="true" />
                Refresh
              </button>
            </div>
          </div>
          <OpenBatchCard batch={openBatch} />
          {listError && <div className="intake-batches__error" role="alert">{listError}</div>}
          {!listPayload.available && !listError && (
            <div className="intake-batches__error" role="status">Promotion batch tables are not available in this environment.</div>
          )}
          <div className="intake-batches__workspace">
            <BatchRail
              batches={listPayload.batches || []}
              selectedId={batchId}
              status={status}
              loading={listLoading}
              onFilterChange={handleFilterChange}
              onSelect={(id) => navigate(`/intake/batches/${id}`)}
              page={listPayload.page || page}
              pages={listPayload.pages || 0}
              onPageChange={setPage} />
            <BatchDetail
              payload={detailPayload}
              loading={detailLoading}
              error={detailError}
              expandedMemberId={expandedMemberId}
              memberFilters={memberFilters}
              memberSort={memberSort}
              onMemberFilterChange={(key, value) => {
                setMemberFilters((current) => ({ ...current, [key]: value }));
                setMemberPage(1);
                setExpandedMemberId(null);
              }}
              onClearMemberFilters={() => {
                setMemberFilters(EMPTY_MEMBER_FILTERS);
                setMemberPage(1);
                setExpandedMemberId(null);
              }}
              onMemberSortChange={(value) => {
                setMemberSort(value);
                setMemberPage(1);
                setExpandedMemberId(null);
              }}
              onToggleRationale={(memberId) => setExpandedMemberId((current) => current === memberId ? null : memberId)}
              onMemberPageChange={setMemberPage}
              selectedLeadId={selectedLeadId}
              runningPipelineLeadId={runningPipelineLeadId}
              onRunRemaining={runRemainingForLead}
              runRemainingDisabledReason={runRemainingDisabledReason}
              onSelectLead={openLeadDrawer} />
          </div>
          <BatchLeadDrawer
            lead={selectedLead}
            batchMember={selectedBatchMember}
            payload={previewPayload}
            loading={previewLoading}
            error={previewError}
            runningPipelineLeadId={runningPipelineLeadId}
            runningStageLeadId={runningStageLeadId}
            onClose={closeLeadDrawer}
            onRunNextStage={runNextStageForLead}
            onRunRemaining={runRemainingForLead}
            runNextStageDisabledReason={runNextStageDisabledReason}
            runRemainingDisabledReason={runRemainingDisabledReason}
            onRestoreToIntake={restoreLeadToIntake}
            restoringLeadId={restoringLeadId} />
        </section>
      </div>
    </div>
  );
}
